import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShippingForm } from './ShippingForm';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { CvsStoreForm } from './CvsStoreForm';
import { CheckoutFormSchema, checkoutFormDefaults } from '@/lib/validation';
import { AlertCircle, Lock } from 'lucide-react';
import useCartStore from '@/store/cartStore';
import { formatPrice, markDiscountCodeAsUsed } from '@/lib/pricing';
import { createPaymentOrder, submitPaymentForm } from '@/lib/api';

export function CheckoutForm({ onSuccess }) {
  const navigate = useNavigate();
  const clearCart = useCartStore((state) => state.clearCart);
  const items = useCartStore((state) => state.items);
  
  // 🛡️ 最終修復：精準選取（Atomic Selection）
  // 只選取需要的原始值，避免嵌套物件引用問題
  const cartTotal = useCartStore((state) => state.getCartSummary().total);
  const discountCode = useCartStore((state) => state.getCartSummary().discountCode);

  const methods = useForm({
    resolver: zodResolver(CheckoutFormSchema),
    defaultValues: checkoutFormDefaults,
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const agreeTerms = watch('agree_terms');
  const paymentMethod = watch('payment_method'); // 監聽付款方式變化
  
  // 🚚 根據付款方式動態計算運費
  const getShippingFee = () => {
    if (paymentMethod === 'cod') {
      // 宅配貨到付款：滿 NT$3,000 免運
      return cartTotal >= 3000 ? 0 : 60;
    }
    if (paymentMethod === 'cvs_pickup_cod' || paymentMethod === 'cvs_pickup_prepaid') {
      // 超商取貨：固定運費 NT$60
      return 60;
    }
    // 其他付款方式：免運
    return 0;
  };
  
  const shippingFee = getShippingFee();
  const finalTotal = cartTotal + shippingFee;

  const onSubmit = async (data) => {
    try {
      // 取得購物車中的第一個商品（單一產品系統）
      const cartItem = items[0];
      
      if (!cartItem) {
        alert('購物車是空的');
        return;
      }
      
      // 建構 API 訂單資料
      const orderData = {
        // 產品資訊
        product_sku: cartItem.sku,
        quantity: cartItem.quantity,
        
        // 收件人資訊
        recipient_name: data.recipient_name,
        recipient_phone: data.recipient_phone,
        recipient_email: data.recipient_email,
        
        // 收件地址
        shipping_city: data.city,
        shipping_district: data.district,
        shipping_zip_code: data.zip_code,
        shipping_address: data.address,
        
        // 付款方式
        payment_method: data.payment_method,
        
        // 超商取貨資訊（如果適用）
        cvs_store_name: data.cvs_store_name || null,
        cvs_store_address: data.cvs_store_address || null,
        
        // 折扣碼
        discount_code: discountCode?.code || null,
        
        // 備註
        customer_notes: data.notes || null,
      };
      
      // 呼叫 API 建立訂單
      console.log('提交訂單資料:', orderData);
      const result = await createPaymentOrder(orderData);
      
      console.log('API 回應:', result);
      
      // 如果有使用折扣碼，標記為已使用（前端紀錄）
      if (discountCode?.code) {
        markDiscountCodeAsUsed(discountCode.code);
      }
      
      // 清空購物車
      clearCart();
      
      // 🚀 貨到付款 (COD) 特殊處理
      if (result.payment_method === 'cod') {
        // 貨到付款：直接跳轉到訂單成功頁面
        navigate(result.redirect_url || `/order/${result.order.order_number}`, { replace: true });
        return;
      }
      
      // 🏦 ATM 轉帳特殊處理
      if (result.payment_method === 'atm') {
        // ATM 轉帳：直接跳轉到訂單頁面（顯示銀行帳號）
        navigate(result.redirect_url || `/order/${result.order.order_number}`, { replace: true });
        return;
      }
      
      // 📦 超商取貨付款特殊處理
      if (result.payment_method === 'cvs_pickup_cod') {
        // 超商取貨付款：直接跳轉到訂單頁面
        navigate(result.redirect_url || `/order/${result.order.order_number}`, { replace: true });
        return;
      }
      
      // 💚 LINE Pay 特殊處理
      if (result.payment_method === 'linepay' && result.payment?.payment_url) {
        // LINE Pay：重導向到 LINE Pay 付款頁面
        window.location.href = result.payment.payment_url;
        return;
      }
      
      // 💳 其他線上付款：提交支付表單到藍新金流
      submitPaymentForm(
        result.payment.gateway_url,
        result.payment.form_data
      );
      
    } catch (error) {
      console.error('訂單提交失敗:', error);
      alert(`訂單提交失敗: ${error.message}`);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 收件資訊 */}
        <Card>
          <CardHeader>
            <CardTitle>收件資訊</CardTitle>
          </CardHeader>
          <CardContent>
            <ShippingForm />
          </CardContent>
        </Card>

                {/* 付款方式 */}
                <Card>
                  <CardHeader>
                    <CardTitle>付款方式</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PaymentMethodSelector />
                    
                    {/* 🚚 宅配貨到付款運費說明 */}
                    {paymentMethod === 'cod' && (
                      <Alert className={`mt-4 ${cartTotal >= 3000 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                        <AlertCircle className={`h-4 w-4 ${cartTotal >= 3000 ? 'text-green-600' : 'text-orange-600'}`} />
                        <AlertDescription className={cartTotal >= 3000 ? 'text-green-800' : 'text-orange-800'}>
                          {cartTotal >= 3000 ? (
                            <span>✅ 貨到付款滿 NT$3,000 免運費！</span>
                          ) : (
                            <span>
                              💰 貨到付款需加收運費 NT$60<br/>
                              再消費 <strong className="text-orange-600">{formatPrice(3000 - cartTotal)}</strong> 即可免運！
                            </span>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    {/* 📦 超商取貨運費說明 */}
                    {(paymentMethod === 'cvs_pickup_cod' || paymentMethod === 'cvs_pickup_prepaid') && (
                      <Alert className="mt-4 bg-blue-50 border-blue-200">
                        <AlertCircle className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800">
                          📦 <strong>超商取貨運費</strong>
                          <br />
                          • 固定運費：NT$60（統一價格）
                          {paymentMethod === 'cvs_pickup_cod' && (
                            <>
                              <br />
                              • 取貨時付款：商品金額 + 運費 $60 + 手續費 $60
                            </>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
                
                {/* 超商門市資訊 */}
                {(paymentMethod === 'cvs_pickup_cod' || paymentMethod === 'cvs_pickup_prepaid') && (
                  <Card>
                    <CardHeader>
                      <CardTitle>超商門市資訊</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CvsStoreForm />
                    </CardContent>
                  </Card>
                )}

        {/* 服務條款 */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Checkbox
                id="agree_terms"
                checked={agreeTerms}
                onCheckedChange={(checked) => 
                  setValue('agree_terms', checked, { shouldValidate: true })
                }
                className={errors.agree_terms ? 'border-red-500' : ''}
              />
              <div className="flex-1">
                <Label
                  htmlFor="agree_terms"
                  className="text-sm cursor-pointer"
                >
                  我已閱讀並同意
                  <a 
                    href="#terms" 
                    className="text-blue-600 hover:underline mx-1"
                    onClick={(e) => e.preventDefault()}
                  >
                    服務條款
                  </a>
                  與
                  <a 
                    href="#privacy" 
                    className="text-blue-600 hover:underline mx-1"
                    onClick={(e) => e.preventDefault()}
                  >
                    隱私權政策
                  </a>
                </Label>
                {errors.agree_terms && (
                  <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.agree_terms.message}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 提交按鈕 */}
        <div className="space-y-3">
          {Object.keys(errors).length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                請檢查並修正表單中的錯誤
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full h-12 text-lg"
            disabled={isSubmitting || !agreeTerms}
          >
            <Lock className="mr-2 h-5 w-5" />
            {isSubmitting ? '處理中...' : `確認訂購 ${formatPrice(finalTotal)}`}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            點擊確認訂購即表示您同意我們的服務條款與隱私權政策
          </p>
        </div>
      </form>
    </FormProvider>
  );
}
